import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { TributeValue, TributeValueType } from '@app/shared/interfaces/lib/tribute.interface';
import { Editor } from '@tiptap/core';
import { BubbleMenu } from '@tiptap/extension-bubble-menu';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import StarterKit from '@tiptap/starter-kit';
import Tribute from 'tributejs';
import { CustomCodeExtension } from './customcode-extension';
import { FillableFieldsExtension } from './fillable-fields-extension';
import { ShortCodeExtension } from './shortcode-extension';
import { TributeEnterHandlerExtension } from './tribute-enter-handler-extension';
import { UserMentionExtension } from './user-mention-extension';

@Component({
  standalone: false,
  selector: 'sbnb-rich-text-editor',
  templateUrl: './rich-text-editor.component.html',
  styleUrls: ['./rich-text-editor.component.scss'],
})
export class RichTextEditorComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input() content: string;
  @Input() placeholderExtension: any;
  @Input() placeholderContent: string;
  @Input() placeholder: string;
  @Input() tributeValues: TributeValue[];
  @Input() tributeFillableFields: TributeValue[];
  @Input() tributeUserMentions: TributeValue[];
  @Input() hideMenu = false;
  @Input() useResponsiveHeight = false;
  @Output() contentUpdated: EventEmitter<string> = new EventEmitter();
  @ViewChild('editorContent', { static: false }) editorContent: ElementRef;
  @ViewChild('bubbleMenu', { static: false }) bubbleMenu: ElementRef;
  public editor: Editor;
  private tribute: any;

  ngOnChanges(changes: SimpleChanges): void {
    // Reinitialize Tribute when tributeUserMentions changes, but only after the editor is ready
    if (changes['tributeUserMentions'] && this.editor && !changes['tributeUserMentions'].firstChange) {
      this.initializeTribute();
    }
  }

  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorContent.nativeElement,
      content: this.content,
      extensions: [
        StarterKit,
        Underline,
        BubbleMenu.configure({
          element: this.bubbleMenu.nativeElement,
        }),
        Link.configure({
          openOnClick: 'whenNotEditable',
          linkOnPaste: true,
          HTMLAttributes: {
            class: 'editor-link',
            target: '_blank',
          },
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right', 'justify'],
        }),
        this.placeholderExtension ? this.placeholderExtension(this.editor) : null,
        ShortCodeExtension,
        FillableFieldsExtension,
        CustomCodeExtension,
        UserMentionExtension,
        TributeEnterHandlerExtension,
        Placeholder.configure({
          placeholder: this.getPlaceholderForExtension(),
        }),
      ],
      onUpdate: ({ editor }) => {
        this.content = editor.getHTML();
        if (this.placeholderContent && this.content.includes(this.placeholderContent)) {
          this.content = this.content.replace(this.placeholderContent, '');
          this.editor.commands.setContent(this.content);
        }
        this.contentUpdated.emit(this.content);
      },
      onFocus: ({ editor }) => {
        this.removePlaceholderContent(editor);
      },
      onBlur: ({ editor }) => {
        this.addPlaceholderContent(editor);
      },
    });

    this.initializeTribute();
  }

  removePlaceholderContent(editor) {
    setTimeout(() => {
      this.content = editor.getHTML();

      if (this.placeholderContent && this.content.includes(this.placeholderContent)) {
        this.content = this.content.replace(this.placeholderContent, '');
        this.editor.commands.setContent(this.content, false);
      }
    }, 200);
  }

  addPlaceholderContent(editor) {
    this.content = editor.getHTML();

    if ((!this.content || this.content === '<p></p>') && this.placeholderContent) {
      this.editor.commands.setContent(this.placeholderContent);
      return;
    }
  }

  ngOnDestroy(): void {
    this.editor.destroy();

    if (this.tribute) {
      const proseMirrorElement = this.editorContent.nativeElement.querySelector('.ProseMirror');
      if (proseMirrorElement) {
        this.tribute.detach(proseMirrorElement);
      }
    }
  }

  private getPlaceholderForExtension(): string {
    // If a custom placeholder is provided, use it
    if (this.placeholder) {
      return this.placeholder;
    }

    // Otherwise, generate a default placeholder based on available tribute collections
    const hasShortCodes = this.tributeValues && this.tributeValues.length > 0;
    const hasFillableFields = this.tributeFillableFields && this.tributeFillableFields.length > 0;
    const hasUserMentions = this.tributeUserMentions && this.tributeUserMentions.length > 0;

    if (hasFillableFields) {
      return 'Write, or press % or { for personalization';
    } else if (hasShortCodes) {
      return 'Write, or press % for personalization';
    } else if (hasUserMentions) {
      return 'Write, or press @ to mention';
    } else {
      return 'Write your message...';
    }
  }

  initializeTribute(): void {
    // Clean up existing tribute instance first
    if (this.tribute) {
      const proseMirrorElement = this.editorContent?.nativeElement?.querySelector('.ProseMirror');
      if (proseMirrorElement) {
        this.tribute.detach(proseMirrorElement);
      }
      this.tribute = null;
    }

    const collections: any[] = [];

    // Add shortcodes and custom codes collection
    if (this.tributeValues && this.tributeValues.length > 0) {
      collections.push({
        values: this.tributeValues,
        trigger: '%',
        replaceTextSuffix: '',
        requireLeadingSpace: false,
        selectTemplate: (item: any) => {
          if (item.original.type === TributeValueType.ShortCode) {
            return `<span class="shortcode" contenteditable="false" >%${item.string}%</span>`;
          }
          if (item.original.type === TributeValueType.CustomCode) {
            return `<span class="customcode" contenteditable="false" >%%${item.string}%%</span>`;
          }
        },
        menuItemTemplate: function (item: any) {
          if (item.original.type === TributeValueType.ShortCode) {
            return `<span class="shortcode" contenteditable="false">%${item.string}%</span>`;
          }
          if (item.original.type === TributeValueType.CustomCode) {
            return `<span class="customcode" contenteditable="false">%%${item.string}%%</span>`;
          }
        },
      });
    }

    // Add fillable fields collection
    if (this.tributeFillableFields && this.tributeFillableFields.length > 0) {
      collections.push({
        values: this.tributeFillableFields,
        trigger: '{',
        replaceTextSuffix: '',
        requireLeadingSpace: false,
        selectTemplate: (item: any) => {
          return `<span class="fillable-field" contenteditable="false" >{${item.string}}</span>`;
        },
        menuItemTemplate: function (item: any) {
          return `<span class="fillable-field" contenteditable="false">{${item.string}}</span>`;
        },
      });
    }

    // Add user mentions collection
    if (this.tributeUserMentions && this.tributeUserMentions.length > 0) {
      collections.push({
        values: this.tributeUserMentions,
        trigger: '@',
        replaceTextSuffix: '',
        requireLeadingSpace: false,
        selectTemplate: (item: any) => {
          return `<span class="user-mention" contenteditable="false" data-uid="${item.original.uid}" data-first-name="${item.original.firstName}" data-last-name="${item.original.lastName}">@${item.string}</span>`;
        },
        menuItemTemplate: function (item: any) {
          return `<span class="user-mention">@${item.string}</span>`;
        },
        lookup: 'key',
      });
    }

    if (collections.length > 0 && this.editorContent?.nativeElement) {
      this.tribute = new Tribute({
        collection: collections,
      });

      const proseMirrorElement = this.editorContent.nativeElement.querySelector('.ProseMirror');
      if (proseMirrorElement) {
        this.tribute.attach(proseMirrorElement);
      }
    }
  }

  setTypography(type: string): void {
    switch (type) {
      case 'paragraph':
        this.editor.chain().focus().setParagraph().run();
        break;
      case 'heading1':
        this.editor.chain().focus().toggleHeading({ level: 1 }).run();
        break;
      case 'heading2':
        this.editor.chain().focus().toggleHeading({ level: 2 }).run();
        break;
      case 'heading3':
        this.editor.chain().focus().toggleHeading({ level: 3 }).run();
        break;
      case 'heading4':
        this.editor.chain().focus().toggleHeading({ level: 4 }).run();
        break;
      case 'heading5':
        this.editor.chain().focus().toggleHeading({ level: 5 }).run();
        break;
    }
  }

  getActiveTypography(): string {
    if (this.editor.isActive('heading', { level: 1 })) {
      return 'heading1';
    } else if (this.editor.isActive('heading', { level: 2 })) {
      return 'heading2';
    } else if (this.editor.isActive('heading', { level: 3 })) {
      return 'heading3';
    } else if (this.editor.isActive('heading', { level: 4 })) {
      return 'heading4';
    } else if (this.editor.isActive('heading', { level: 5 })) {
      return 'heading5';
    } else {
      return 'paragraph';
    }
  }

  toggleLink(): void {
    const previousUrl = this.editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl);

    // If the user cancels the prompt, return early
    if (url === null) {
      return;
    }

    // If the URL is empty, remove the link
    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }

    // Otherwise, set the link with the provided URL
    this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  }

  toggleBulletList(): void {
    this.editor.chain().focus().toggleBulletList().run();
  }

  toggleOrderedList(): void {
    this.editor.chain().focus().toggleOrderedList().run();
  }

  alignText(alignment: 'left' | 'center' | 'right' | 'justify'): void {
    this.editor.chain().focus().setTextAlign(alignment).run();
  }

  selectPersonalizationType(type: 'shortcode' | 'fillable' | 'userMention'): void {
    if (!this.editor || !this.tribute) return;

    // Focus the editor first
    this.editor.commands.focus();

    // Get the ProseMirror element that tribute is attached to
    const proseMirrorElement = this.editorContent.nativeElement.querySelector('.ProseMirror');

    let triggerChar: string;
    let collectionIndex: number;

    if (type === 'shortcode') {
      triggerChar = '%';
      collectionIndex = 0;
    } else if (type === 'fillable') {
      triggerChar = '{';
      // Calculate index based on what's available
      collectionIndex = this.tributeValues && this.tributeValues.length > 0 ? 1 : 0;
    } else if (type === 'userMention') {
      triggerChar = '@';
      // Calculate index based on what's available
      let index = 0;
      if (this.tributeValues && this.tributeValues.length > 0) index++;
      if (this.tributeFillableFields && this.tributeFillableFields.length > 0) index++;
      collectionIndex = index;
    }

    // Try natural typing simulation first, with fallback to showMenuForCollection
    this.simulateNaturalTyping(proseMirrorElement, triggerChar);

    // Fallback: if menu doesn't open in 100ms, use showMenuForCollection
    setTimeout(() => {
      if (!this.tribute.isActive) {
        this.tribute.showMenuForCollection(proseMirrorElement, collectionIndex);
      }
    }, 100);
  }

  // This has been the way I've been able to get the tiptap editor to dynamically open the personalization menu
  private simulateNaturalTyping(element: HTMLElement, character: string): void {
    // First insert the character via Tiptap
    this.editor.commands.insertContent(character);

    // Simulate the full sequence of events that real typing would generate
    setTimeout(() => {
      const charCode = character.charCodeAt(0);

      // Keydown event
      const keydownEvent = new KeyboardEvent('keydown', {
        key: character,
        code: `Key${character.toUpperCase()}`,
        keyCode: charCode,
        which: charCode,
        bubbles: true,
        cancelable: true,
      });

      // Input event
      const inputEvent = new InputEvent('input', {
        inputType: 'insertText',
        data: character,
        bubbles: true,
        cancelable: true,
      });

      // Keyup event
      const keyupEvent = new KeyboardEvent('keyup', {
        key: character,
        code: `Key${character.toUpperCase()}`,
        keyCode: charCode,
        which: charCode,
        bubbles: true,
        cancelable: true,
      });

      // Dispatch in sequence
      element.dispatchEvent(keydownEvent);
      element.dispatchEvent(inputEvent);
      element.dispatchEvent(keyupEvent);
    }, 10);
  }

  /**
   * Clears the editor content and emits the empty content
   */
  public clearContent(): void {
    if (this.editor) {
      this.editor.commands.clearContent();
      this.content = '';
      this.contentUpdated.emit('');
    }
  }
}
